import 'containers/SmWorkbench/ProjectDetail/Show/Detail/Bottom/RequotePrice/index.less';
import * as React from 'react';
import PropTypes from 'prop-types';
import { Button, Table, InputNumber, Icon, Form, Input } from '@beehive/components';
import { Checkbox, Button as BCButton, Space } from "@bedrock/components";
import { TabContent } from 'containers/SmWorkbench/ProjectDetail/Show/Detail/Bottom/components';
import { isBlank, showAlert } from 'utils';
import classnames from 'classnames';
import { financial } from 'utils/number';
import _ from 'lodash';
import messages from 'messages/Workbench/ProjectDetail';
import { getCooperateAccountsQuoteData, getTranslatedFormItem } from 'containers/SmWorkbench/ProjectDetail/utils';
import { hasGrayConfigWithoutGroup } from 'utils/gray';
import NewPendingQuote from '@mpc/common/src/features/NewPendingQuote';
import StatementModal from 'containers/OutWorkbench/components/StatementModal';
import { tableColumnWidthToRem } from '@mpc/common/src/utils';

export interface PendingQuoteProps {
  tabKey: string;
}

const TABLE_COLUMNS = [
  { key: 'index', title: 'index', align: 'left', width: 120 },
  { key: 'platform', title: 'superior_resource_id', align: 'left', width: 110 },
  { key: 'account', title: 'account', align: 'left', width: 110 },
  { key: 'remark', title: 'remark', align: 'left', width: 100 },
  { key: 'quote_price_origin', title: 'quote_price_origin', align: 'right', width: 140 },
  { key: 'arrow', title: '', align: 'center', width: 64 },
  { key: 'quote_price', title: 'quote_price_changed', align: 'left', width: 140 },
  { key: 'translated_currency', title: 'currency', align: 'left', width: 100 },
];
const FULL_COL_PROPS = {
  itemColProps: { span: 24 },
  itemLabelColProps: { span: 4 },
  itemInputColProps: { span: 20 },
};
export const FormProps = {
  mode: 'edit',
  isLabelVertical: false,
  showClientError: false,
  checkMinHeight: false,
  ...FULL_COL_PROPS,
};
const FormItems: any[] = [
  {
    key: 'remark',
    label: 'requoteNote',
    childProps: {
      type: 'textArea',
      maxLength: 800,
      maxHeight: 76,
      minHeight: 76,
      autosize: true,
    },
  },
];

class PendingQuote extends React.Component<PendingQuoteProps, any> {

  public static contextTypes = {
    intl: PropTypes.object,
    prefixCls: PropTypes.string,
    menuKey: PropTypes.string,
    detail: PropTypes.any,

    loadDetail: PropTypes.func,
    projectMove: PropTypes.func,
  };

  private tableRef: React.RefObject<typeof Table> = React.createRef();
  private quoteRef: React.RefObject<any> = React.createRef();

  constructor(props: PendingQuoteProps, context: any) {
    super(props, context);

    const { intl, detail } = context;
    const data = getCooperateAccountsQuoteData(detail, intl, messages);
    const form = data.filter((i: any) => i.is_win || i.isTotal).map((i: any, index: number) => {
      return { ...i, index: index + 1, quote_price_origin: i.quote_price };
    });

    this.state = {
      form,
      formExtra: {},
      hasChanged: false,
      submitting: false,
      newPendingQuoteParams: {},
      visible: false,
      isSelected: false,
    };
  }

  private handleSubmit = async () => {
    const { menuKey, detail, loadDetail, projectMove } = this.context;
    const { form = [], formExtra = {}, isSelected, visible } = this.state;
    const params = form.filter((i: any) => !i.isTotal).filter((i: any) => !isBlank(i.quote_price));
    const data = await this.quoteRef.current?.validateFields()
    const value = this.state.newPendingQuoteParams
    const bid_attachs_blob_keys = data?.bid_attachs?.map((item: any) => item.blob?.key)
    const merge_quote_attach_blob_keys = detail?.quote_upload_method === 'MULTI_UPLOAD' ? 
       data?.merge_quote_attachs?.map((item: any) => item.blob?.blob_key || item.blob?.key) : undefined;  

    if (!isSelected) {
      this.setState({ visible: true })
      return
    }

    if (this.tableRef.current && !this.tableRef.current.valid()) {
      return;
    }

    let body: any = {
      id: detail.id,
      making: {
        is_pass: false,
        ...formExtra,
        demand_suppliers_attributes: {
          id: detail.current_demand_supplier_id,
          demand_supplier_quotes_attributes: params.map((i: any) => {
            return { id: i.quote_id, requote_price: i.quote_price };
          }),
        },
      },
    };

    if (hasGrayConfigWithoutGroup(['external.demand_flow.requote_price'])) {
      if (detail?.structured) {
        body = {
          id: detail.id,
          state: 'making',
          pass: false,
          form: {
            id: detail.id,
            remark: value?.remark,
            demand_supplier: {
              ...value,
              demand_supplier_quotes: value?.demand_supplier_quotes?.filter((item: any) => item.ableQuote)?.map(v => {
                return {
                  ...v,
                  necessary_rights: v?.cooperate_account?.necessary_rights,
                  expect_rights: v?.cooperate_account?.expect_rights,
                }
              }) || [],
              is_quoted: true,
              id: detail.current_demand_supplier_id,
              bid_attachs_blob_keys,
              merge_quote_attach_blob_keys,
            }
          },
        }
      } else {
        body = {
          id: detail.id,
          state: 'making',
          pass: false,
          form: {
            id: detail.id,
            ...formExtra,
            demand_supplier: {
              id: detail.current_demand_supplier_id,
              demand_supplier_quotes: params.map((i: any) => {
                return { id: i.quote_id, requote_price: i.quote_price };
              }),
            },
          },
        };
      }
    }

    this.setState({ submitting: true });
    return projectMove(body, { menuKey, grayKey: 'requote_price' })
      .then((res: any) => {
        showAlert({ type: 'success', message: res.res_msg });
        this.setState({ submitting: false });
        loadDetail();
      }).catch((error: any) => {
        showAlert({ type: 'error', message: typeof error === 'string' ? error : error.message });
        this.setState({ submitting: false });
      });
  }

  private getTotalPrice = (data: any[]) => {
    const total = data.filter((i: any) => !i.isTotal)
      .reduce((prev: number, curr: any) => {
        const price = curr['quote_price'];
        prev = _.add(prev, isBlank(price) ? 0 : +price);
        return prev;
      }, 0);
    return total;
  }

  private handleChange = (key: string, index: number, value: any) => {
    const { form = [] } = this.state;
    const newForm = [...form];
    newForm[index][key] = value;
    newForm[newForm.length - 1]['quote_price'] = this.getTotalPrice(newForm);
    const hasChanged = newForm.some((i: any) => i.quote_price !== i.quote_price_origin);
    this.setState({ form: newForm, hasChanged });
  }

  private handleFormChange = (key: string, value: any) => {
    const { formExtra = {} } = this.state;
    const newForm = { ...formExtra };
    newForm[key] = value;
    this.setState({ formExtra: newForm });
  }

  private renderFormItemChild = (itemProps: any, childProps: any) => {
    const { formExtra = {} } = this.state;
    const { key } = itemProps;
    const { type, ...childPropsRest } = childProps;
    switch (type) {
      case 'textArea': {
        return (
          <Input.TextArea
            {...childPropsRest}
            value={isBlank(formExtra[key]) ? '' : formExtra[key]}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              this.handleFormChange(key, evt.target.value);
            }}
          />
        );
      }
      default: {
        return <span />;
      }
    }
  }

  private getColumnDefaultRender = () => {
    return (v: any, r: any, i: number) => {
      return isBlank(v) ? r.isTotal ? '' : '--' : v;
    };
  }

  private getColumnRender = () => {
    const { intl } = this.context;
    const { hasChanged } = this.state;
    return {
      index: (v: any, r: any, i: number) => {
        return r.isTotal ? intl.formatMessage(messages.quote_price_total) : isBlank(v) ? '--' : v;
      },
      quote_price_origin: (v: any, r: any, i: number) => {
        return isBlank(v) ? '--' : financial(v);
      },
      arrow: (v: any, r: any, i: number) => {
        let isChanged = r['quote_price_origin'] !== r['quote_price'];
        if (r.isTotal) {
          isChanged = hasChanged;
        }
        return isChanged ? <Icon name="arrow-up" /> : '';
      },
      quote_price: (v: any, r: any, i: number) => {
        if (r.isTotal) {
          return isBlank(v) ? '--' : (
            <span className={hasChanged ? 'quote-changed' : ''}>{financial(v)}</span>
          );
        }
        return (
          <InputNumber
            hideHandle
            min={0}
            precision={2}
            placeholder={intl.formatMessage(messages.inputPlaceholder)}
            value={v}
            formatter={(val: any) => financial(val, true)}
            onChange={(value: any) => this.handleChange('quote_price', i, value)}
          />
        );
      },
    };
  }

  private getColumns = () => {
    const { intl } = this.context;
    const defaultRender = this.getColumnDefaultRender();
    const columRenders = this.getColumnRender();
    return TABLE_COLUMNS.map((item: any) => {
      const title = !isBlank(item.title) ? intl.formatMessage(messages[item.title]) : '';
      return {
        ...tableColumnWidthToRem(item),
        dataIndex: item.key,
        title,
        name: title,
        render: columRenders[item.key] || defaultRender,
        required: item.key === 'quote_price',
        className: classnames({
          [item.key]: true,
        }),
      };
    });
  }
  private handleCheckBoxChange = (isSelected: boolean) => {
    this.setState({ isSelected })
  }
  private handleOpenModal = () => {
    this.setState({ visible: true })
  }
  private renderBottom = () => {
    const { intl } = this.context;
    const { hasChanged, submitting, isSelected } = this.state;
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 20 }}>
        <Space size={4}>
          <Checkbox checked={isSelected} onChange={e => {
            this.handleCheckBoxChange(e.target.checked)
          }}>
            我已阅读并同意
          </Checkbox>
          <BCButton type='link' onClick={this.handleOpenModal}>《报价声明》</BCButton>
        </Space>
        <Button
          className="pull-right"
          size="large"
          type="primary"
          disabled={!hasChanged}
          loading={submitting}
          onClick={this.handleSubmit}
        >
          {intl.formatMessage(messages.submit)}
        </Button>
      </div>
    );
  }

  componentDidMount(): void {
    const { detail } = this.context;
    this.setState({ newPendingQuoteParams: detail?.demand_suppliers?.[0] })
  }

  public render() {
    const { intl, detail } = this.context;
    const { tabKey } = this.props;
    const { form, visible } = this.state;

    const bottom = this.renderBottom();
    const columns = this.getColumns();


    return (
      <TabContent tabKey={tabKey} bottom={bottom} canScroll>
        {
          detail?.structured ? <NewPendingQuote
            detail={detail}
            type='making'
            ref={this.quoteRef}
            showLastRound={true}
            value={this.state.newPendingQuoteParams}
            onlyWinning={true}
            onChange={value => {
              this.setState({ hasChanged: true })
              this.setState({ newPendingQuoteParams: value })
            }}
          /> : (
            <>
              <Table
                ref={this.tableRef}
                columns={columns}
                data={form}
                size="small"
                rowKey="index"
              />
              <Form {...FormProps}>
                {FormItems.map((item: any) => {
                  const translatedItem = getTranslatedFormItem(item, intl, messages);
                  const { childProps, ...itemProps } = translatedItem;
                  return (
                    <Form.Item
                      {...itemProps}
                      key={itemProps.key}
                      itemKey={itemProps.formKey || itemProps.key}
                      className={itemProps.key}
                    >
                      {this.renderFormItemChild(itemProps, childProps)}
                    </Form.Item>
                  );
                })}
              </Form>
            </>
          )
        }
        <StatementModal visible={visible} onCancel={() => {
          this.setState({ visible: false, isSelected: false })
        }} onOk={() => {
          this.setState({ visible: false, isSelected: true }, () => {
            this.handleSubmit()
          })
        }} />
      </TabContent>
    );
  }

}

export default PendingQuote;
